import { USER_TYPING_DEBOUNCE_TIME } from '@helpcenter/constants';
import { debounce, isNil } from 'lodash';
import { fetchUsersJsonWithIssueKey } from 'rest/mention-provider';
import type { UserQuery } from 'rest/user';
import { searchUser } from 'rest/user';
import type { MentionsResult, MentionDescription } from '@atlaskit/mention';
import { AbstractMentionResource } from '@atlaskit/mention';

export type FieldConfigs = Pick<UserQuery, 'fieldConfigId' | 'fieldName' | 'portalId'>;

export const getMentionProvider = (loggedInUserAccountId: string, requestKey?: string, fieldConfigs?: FieldConfigs) =>
    new MentionProviderSelector(loggedInUserAccountId, requestKey, fieldConfigs);

class MentionProviderSelector extends AbstractMentionResource {
    issueKey?: string;
    fieldConfigs?: FieldConfigs;
    loggedInAccountId: string;
    lastSearchTime: number;
    activeSearches: Set<string>;

    constructor(loggedInAccountId: string, issueKey?: string, fieldConfigs?: FieldConfigs) {
        super();
        if (issueKey) {
            this.issueKey = issueKey;
        }
        if (fieldConfigs) {
            this.fieldConfigs = fieldConfigs;
        }
        this.loggedInAccountId = loggedInAccountId;
        this.lastSearchTime = 0;
        this.activeSearches = new Set();
    }

    isFiltering = (query: string) => {
        return this.activeSearches?.has(query);
    };

    filter = (query?: string) => {
        return this.debouncedFilter(query);
    };

    notifyListeners = (mentionResult: MentionsResult, duration: number) => {
        this._notifyListeners(mentionResult, {
            duration,
        });
    };

    notifyAllResultsListeners = (mentionResult: MentionsResult) => {
        this._notifyListeners(mentionResult);
    };

    notifyErrorListeners = (error: Error) => {
        this._notifyErrorListeners(error);
    };

    debouncedFilter = debounce((query: string = '') => {
        const currentSearchTime = Date.now();
        return this.search(query)
            .then((mentionResult) => {
                this.activeSearches.delete(query);
                // discard the response from previous requests
                if (currentSearchTime > this.lastSearchTime) {
                    this.lastSearchTime = currentSearchTime;

                    this.notifyListeners(mentionResult, Date.now() - currentSearchTime);
                }

                this.notifyAllResultsListeners(mentionResult);
            })
            .catch((e) => {
                if (!isNil(query) && query.length > 0) {
                    this.activeSearches.delete(query);
                }
                this.notifyErrorListeners(e);
            });
    }, USER_TYPING_DEBOUNCE_TIME);

    shouldHighlightMention = (mention: MentionDescription) => mention.id === this.loggedInAccountId;

    search = (query: string = ''): Promise<MentionsResult> => {
        this.activeSearches.add(query);

        if (this.issueKey) {
            return fetchUsersJsonWithIssueKey(this.issueKey, query)
                .mergeMap((users) => users)
                .reduce((allMentions, mention) => [...allMentions, mention], [])
                .map((mentions) => ({ mentions, query }))
                .toPromise();
        }

        if (this.fieldConfigs) {
            return searchUser({
                query,
                ...this.fieldConfigs,
            })
                .map((response) =>
                    response.map((user) => ({
                        id: user.id,
                        name: user.displayName,
                        avatarUrl: user.avatar,
                    }))
                )
                .mergeMap((users) => users)
                .reduce((allMentions, mention) => [...allMentions, mention], [])
                .map((mentions) => ({ mentions, query }))
                .toPromise();
        }
        return Promise.resolve({ query, mentions: [] });
    };
}

export default MentionProviderSelector;
